import { FileText, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "../Button";

export const Dropzone = ({ onFileSelect, multiple = false, value }) => {
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (value === null) {
      setFiles([]);
    } else if (Array.isArray(value)) {
      setFiles(value);
    } else if (value) {
      setFiles([value]);
    }
  }, [value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/msword": [".doc"],
    },
    onDrop: (acceptedFiles) => {
      if (multiple) {
        const updatedFiles = [...files, ...acceptedFiles];
        setFiles(updatedFiles);
        onFileSelect(updatedFiles);
      } else {
        const singleFile = acceptedFiles[0] ? [acceptedFiles[0]] : [];
        setFiles(singleFile);
        onFileSelect(singleFile);
      }
    },
    multiple,
  });

  const removeFile = (file) => {
    const newFiles = files.filter((f) => f !== file);
    setFiles(newFiles);
    if (newFiles.length === 0) {
      onFileSelect(null);
    }
  };

  const getFileIcon = (fileType) => {
    const iconClasses = "h-5 w-5";

    switch (fileType) {
      case "application/pdf":
        return <FileText className={`${iconClasses} text-red-500`} />;
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/msword":
        return <FileText className={`${iconClasses} text-blue-500`} />;
      default:
        return <FileText className={`${iconClasses} text-gray-500`} />;
    }
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`
            flex h-64 w-full cursor-pointer flex-col items-center justify-center 
            rounded-lg border-2 border-dashed 
            ${
              isDragActive
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-gray-50"
            }
            transition-colors duration-200
          `}
      >
        <input {...getInputProps()} />
        <Upload className="h-12 w-12 mb-4 text-gray-400" />
        <div className="text-center">
          <p className="mb-2 text-lg font-medium text-gray-900">
            {isDragActive
              ? "Drop your resume here"
              : "Drag & drop your resume, or click to select"}
          </p>
          <p className="text-sm text-gray-500">Supports PDF files only</p>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((file, index) => (
            <li
              key={index}
              className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3"
            >
              <div className="flex items-center space-x-3">
                {getFileIcon(file.type)}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="p-1 hover:bg-gray-200"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(file);
                }}
              >
                <X className="h-4 w-4 text-gray-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
